import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatabaseSoundPackModel, DatabaseSoundPack, PackInfoStyle } from '../../services/DatabaseSoundPack';
import { useSoundPacks } from '../../context/SoundPacksContext';
import { isoToDatetimeLocal, datetimeLocalToIso } from '../../helpers/datetimeLocal';
import { validateHLSFiles, editManifestFile } from '../../helpers/hlsVideo';

type PackInputs = { name: string; description: string; priceId: string; price: string; infoPageStyle: PackInfoStyle; secondaryTitle: string; createdAt: string };
type PackFiles = { coverFile: File | null; thumbnailFile: File | null; fullWidthFile: File | null; videoFiles: File[] };

// Asset-completeness guard for the info-page variant. Each style needs one
// media asset to render its hero section; assigning a style without it produces
// a broken page in the plugin. Returns the missing asset's label, or null when
// the style's required asset is present — either already on the pack or freshly
// selected in this edit session (so the imminent upload counts).
//   style 1 → HLS video + its thumbnail (the plugin only shows the player once
//             both exist; both uploadable below)
//   style 2 → full-width image (uploadable below)
function missingAssetForStyle(
    style: PackInfoStyle,
    pack: DatabaseSoundPack | undefined,
    files: PackFiles | undefined,
): string | null {
    if (style === 'style2') {
        return pack?.fullWidthImageUrl || files?.fullWidthFile ? null : 'a full-width image';
    }
    if (!pack?.videoPlaylistUrl && !files?.videoFiles.length) return 'an HLS video folder';
    if (!pack?.videoThumbnailUrl && !files?.thumbnailFile) return 'a video thumbnail';
    return null;
}

function EditPreview() {
    const navigate = useNavigate();
    const { soundPacks: allPacks, isLoading: loading } = useSoundPacks();
    const [updating, setUpdating] = useState<string | null>(null);
    const [updateProgress, setUpdateProgress] = useState('');
    const [editInputs, setEditInputs] = useState<Record<string, PackInputs>>({});
    const [fileInputs, setFileInputs] = useState<Record<string, PackFiles>>({});
    const [thumbnailErrors, setThumbnailErrors] = useState<Record<string, string | null>>({});
    const [hlsErrors, setHlsErrors] = useState<Record<string, string | null>>({});
    const [hlsWarnings, setHlsWarnings] = useState<Record<string, string | null>>({});

    // allPacks already arrives newest-first from SoundPacksContext.
    const soundPacks = useMemo(
        () => allPacks.filter(p => !p.public),
        [allPacks]
    );

    // Initialize edit state for packs we haven't seen yet. Don't overwrite
    // existing entries — that would clobber the admin's in-progress edits when
    // the subscription fires after an update or unrelated change.
    useEffect(() => {
        setEditInputs(prev => {
            let changed = false;
            const next = { ...prev };
            for (const pack of soundPacks) {
                if (!pack.id || next[pack.id]) continue;
                next[pack.id] = {
                    name: pack.name,
                    description: pack.description || '',
                    priceId: pack.priceId || '',
                    price: pack.price || '',
                    infoPageStyle: pack.infoPageStyle || 'style1',
                    secondaryTitle: pack.secondaryTitle || '',
                    createdAt: isoToDatetimeLocal(pack.createdAt),
                };
                changed = true;
            }
            return changed ? next : prev;
        });
        setFileInputs(prev => {
            let changed = false;
            const next = { ...prev };
            for (const pack of soundPacks) {
                if (!pack.id || next[pack.id]) continue;
                next[pack.id] = { coverFile: null, thumbnailFile: null, fullWidthFile: null, videoFiles: [] };
                changed = true;
            }
            return changed ? next : prev;
        });
    }, [soundPacks]);

    const handleInputChange = (packId: string, field: keyof PackInputs, value: string) => {
        setEditInputs(prev => ({
            ...prev,
            [packId]: { ...prev[packId], [field]: value }
        }));
    };

    const validateThumbnailResolution = (file: File): Promise<{ valid: boolean; error?: string }> => {
        return new Promise((resolve) => {
            const img = new Image();
            const url = URL.createObjectURL(file);

            img.onload = () => {
                URL.revokeObjectURL(url);
                if (img.width === 1920 && img.height === 1080) {
                    resolve({ valid: true });
                } else {
                    resolve({
                        valid: false,
                        error: `Resolution must be 1920x1080 (found ${img.width}x${img.height})`
                    });
                }
            };

            img.onerror = () => {
                URL.revokeObjectURL(url);
                resolve({ valid: false, error: 'Failed to load image' });
            };

            img.src = url;
        });
    };

    const handleCoverChange = (packId: string, file: File | null) => {
        setFileInputs(prev => ({
            ...prev,
            [packId]: { ...prev[packId], coverFile: file }
        }));
    };

    const handleFullWidthChange = (packId: string, file: File | null) => {
        setFileInputs(prev => ({
            ...prev,
            [packId]: { ...prev[packId], fullWidthFile: file }
        }));
    };

    const handleVideoFolderChange = async (packId: string, fileList: FileList | null) => {
        const files = fileList ? Array.from(fileList) : [];
        if (files.length === 0) {
            setFileInputs(prev => ({ ...prev, [packId]: { ...prev[packId], videoFiles: [] } }));
            setHlsErrors(prev => ({ ...prev, [packId]: null }));
            setHlsWarnings(prev => ({ ...prev, [packId]: null }));
            return;
        }

        const validation = await validateHLSFiles(files);
        setHlsErrors(prev => ({ ...prev, [packId]: validation.valid ? null : (validation.error || 'Invalid HLS files') }));
        setHlsWarnings(prev => ({ ...prev, [packId]: validation.bitrateWarning ?? null }));
        // An invalid folder is dropped, so it can't satisfy the style-1 guard.
        setFileInputs(prev => ({
            ...prev,
            [packId]: { ...prev[packId], videoFiles: validation.valid ? files : [] }
        }));
    };

    const handleThumbnailChange = async (packId: string, file: File | null) => {
        if (!file) {
            setFileInputs(prev => ({
                ...prev,
                [packId]: { ...prev[packId], thumbnailFile: null }
            }));
            setThumbnailErrors(prev => ({ ...prev, [packId]: null }));
            return;
        }

        if (file.type !== 'image/jpeg') {
            setThumbnailErrors(prev => ({ ...prev, [packId]: 'File must be a JPG image' }));
            setFileInputs(prev => ({
                ...prev,
                [packId]: { ...prev[packId], thumbnailFile: null }
            }));
            return;
        }

        const validation = await validateThumbnailResolution(file);
        if (!validation.valid) {
            setThumbnailErrors(prev => ({ ...prev, [packId]: validation.error || 'Invalid thumbnail' }));
            setFileInputs(prev => ({
                ...prev,
                [packId]: { ...prev[packId], thumbnailFile: null }
            }));
        } else {
            setThumbnailErrors(prev => ({ ...prev, [packId]: null }));
            setFileInputs(prev => ({
                ...prev,
                [packId]: { ...prev[packId], thumbnailFile: file }
            }));
        }
    };

    const handleUpdate = async (packId: string) => {
        const inputs = editInputs[packId];
        const files = fileInputs[packId];

        if (!inputs.name.trim()) {
            alert('Name cannot be empty');
            return;
        }

        if (thumbnailErrors[packId]) {
            alert('Please fix thumbnail validation errors before updating');
            return;
        }

        if (hlsErrors[packId]) {
            alert('Please fix the HLS video errors before updating');
            return;
        }

        const newCreatedAt = datetimeLocalToIso(inputs.createdAt);
        if (!newCreatedAt) {
            alert('Invalid created date');
            return;
        }

        const pack = soundPacks.find(p => p.id === packId);

        // Asset-completeness guard: refuse to assign a variant whose hero asset
        // is missing, which would render a broken info page in the plugin.
        const missingAsset = missingAssetForStyle(inputs.infoPageStyle, pack, files);
        if (missingAsset) {
            alert(`Layout "${inputs.infoPageStyle}" needs ${missingAsset}. Add it (or pick another layout) before updating.`);
            return;
        }

        setUpdating(packId);
        try {

            // Update name if changed
            if (pack && inputs.name !== pack.name) {
                await DatabaseSoundPackModel.updateName(packId, inputs.name.trim());
            }

            // Update description if changed
            if (inputs.description.trim() !== (pack?.description || '')) {
                if (inputs.description.trim()) {
                    await DatabaseSoundPackModel.updateDescription(packId, inputs.description.trim());
                }
            }

            // Update style2 secondary title if changed
            if (inputs.secondaryTitle.trim() !== (pack?.secondaryTitle || '')) {
                await DatabaseSoundPackModel.updateSecondaryTitle(packId, inputs.secondaryTitle.trim());
            }

            // Update price ID if changed
            if (inputs.priceId.trim() !== (pack?.priceId || '')) {
                if (inputs.priceId.trim()) {
                    await DatabaseSoundPackModel.updatePriceId(packId, inputs.priceId.trim());
                }
            }

            // Update price if changed
            if (inputs.price !== pack?.price) {
                if (inputs.price && inputs.price.trim()) {
                    await DatabaseSoundPackModel.updatePrice(packId, inputs.price.trim());
                }
            }

            // Update info-page layout variant if changed
            if (inputs.infoPageStyle !== (pack?.infoPageStyle || 'style1')) {
                await DatabaseSoundPackModel.setInfoPageStyle(packId, inputs.infoPageStyle);
            }

            // Update createdAt (drives display ordering) if changed
            if (newCreatedAt !== pack?.createdAt) {
                await DatabaseSoundPackModel.updateCreatedAt(packId, newCreatedAt);
            }

            // Upload new cover image if selected
            const mediaUrls: { coverImageUrl?: string; videoPlaylistUrl?: string; videoThumbnailUrl?: string; fullWidthImageUrl?: string } = {};

            if (files?.coverFile) {
                setUpdateProgress('Uploading cover image...');
                const coverUrl = await DatabaseSoundPackModel.uploadFile(
                    packId,
                    files.coverFile,
                    'cover.png',
                    (percent) => setUpdateProgress(`Uploading cover image (${percent}%)...`)
                );
                mediaUrls.coverImageUrl = coverUrl;
            }

            // Upload new full-width image (style2 hero) if selected
            if (files?.fullWidthFile) {
                setUpdateProgress('Uploading full-width image...');
                const fullWidthUrl = await DatabaseSoundPackModel.uploadFile(
                    packId,
                    files.fullWidthFile,
                    'full_width.png',
                    (percent) => setUpdateProgress(`Uploading full-width image (${percent}%)...`)
                );
                mediaUrls.fullWidthImageUrl = fullWidthUrl;
            }

            // Upload new HLS video (style 1 hero) if selected
            if (files?.videoFiles.length) {
                setUpdateProgress('Editing manifest file...');
                const editedVideoFiles = await editManifestFile(files.videoFiles, packId);
                const total = editedVideoFiles.length;
                setUpdateProgress(`Uploading HLS video files (${total} files) (0%)...`);
                mediaUrls.videoPlaylistUrl = await DatabaseSoundPackModel.uploadHLSVideo(
                    packId,
                    editedVideoFiles,
                    (percent) => setUpdateProgress(`Uploading HLS video files (${total} files) (${percent}%)...`)
                );
            }

            // Upload new video thumbnail if selected
            if (files?.thumbnailFile) {
                setUpdateProgress('Uploading video thumbnail...');
                const thumbnailUrl = await DatabaseSoundPackModel.uploadFile(
                    packId,
                    files.thumbnailFile,
                    'video/thumbnail.jpg',
                    (percent) => setUpdateProgress(`Uploading video thumbnail (${percent}%)...`)
                );
                mediaUrls.videoThumbnailUrl = thumbnailUrl;
            }

            // Update cached media URLs if any files were uploaded
            if (Object.keys(mediaUrls).length > 0) {
                setUpdateProgress('Caching media URLs...');
                await DatabaseSoundPackModel.updateMediaUrls(packId, mediaUrls);
            }

            setUpdateProgress('');
            alert('Sound pack updated successfully!');

            // Drop this pack's local edit state — the subscription will refire
            // with fresh server data and the init effect will re-seed it.
            setHlsErrors(prev => ({ ...prev, [packId]: null }));
            setHlsWarnings(prev => ({ ...prev, [packId]: null }));
            setEditInputs(prev => {
                const { [packId]: _, ...rest } = prev;
                return rest;
            });
            setFileInputs(prev => {
                const { [packId]: _, ...rest } = prev;
                return rest;
            });

        } catch (error) {
            console.error('Error updating sound pack:', error);
            setUpdateProgress('');
            alert('Failed to update: ' + (error instanceof Error ? error.message : 'Unknown error'));
        } finally {
            setUpdating(null);
        }
    };


    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (soundPacks.length === 0) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-500 mb-4">No sound packs found in preview/dev environment.</p>
                <p className="text-sm text-gray-400">Upload a sound pack first in the "Upload Sound Pack" tab.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {soundPacks.map((pack) => {
                const selectedStyle = editInputs[pack.id!]?.infoPageStyle || 'style1';
                const styleWarning = missingAssetForStyle(selectedStyle, pack, fileInputs[pack.id!]);
                const createdAtInvalid = !datetimeLocalToIso(editInputs[pack.id!]?.createdAt || '');
                return (
                <div key={pack.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">{pack.name}</h3>
                            <p className="text-xs text-gray-500 mt-1">
                                ID: <code className="bg-gray-100 px-2 py-1 rounded">{pack.id}</code>
                            </p>
                        </div>
                        <button
                            onClick={() => navigate(`/admin/reorder-sounds/${pack.id}`)}
                            className="ml-4 shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Reorder sounds
                        </button>
                    </div>

                    <div className="space-y-3">
                        {/* Created At Input — drives display ordering, see sortByCreatedAtDesc */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Created Date (controls list ordering)
                            </label>
                            <input
                                type="datetime-local"
                                value={editInputs[pack.id!]?.createdAt || ''}
                                onChange={(e) => handleInputChange(pack.id!, 'createdAt', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                disabled={updating === pack.id}
                            />
                            {createdAtInvalid && (
                                <p className="text-red-600 font-medium text-xs mt-1">Invalid date</p>
                            )}
                        </div>

                        {/* Name Input */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Sound Pack Name
                            </label>
                            <input
                                type="text"
                                value={editInputs[pack.id!]?.name || ''}
                                onChange={(e) => handleInputChange(pack.id!, 'name', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                disabled={updating === pack.id}
                            />
                        </div>

                        {/* Description Input */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Description
                            </label>
                            <textarea
                                value={editInputs[pack.id!]?.description || ''}
                                onChange={(e) => handleInputChange(pack.id!, 'description', e.target.value)}
                                placeholder="A brief description..."
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                disabled={updating === pack.id}
                            />
                        </div>

                        {/* Price ID Input */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Stripe Price ID
                            </label>
                            <input
                                type="text"
                                value={editInputs[pack.id!]?.priceId || ''}
                                onChange={(e) => handleInputChange(pack.id!, 'priceId', e.target.value)}
                                placeholder="price_1234567890abcdef"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                disabled={updating === pack.id}
                            />
                        </div>

                        {/* Price Input */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Price (in USD, without $)
                            </label>
                            <input
                                type="text"
                                value={editInputs[pack.id!]?.price || ''}
                                onChange={(e) => handleInputChange(pack.id!, 'price', e.target.value)}
                                placeholder="9.99"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                disabled={updating === pack.id}
                            />
                        </div>

                        {/* Info Page Layout Variant */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Info Page Layout
                            </label>
                            <select
                                value={selectedStyle}
                                onChange={(e) => handleInputChange(pack.id!, 'infoPageStyle', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                disabled={updating === pack.id}
                            >
                                <option value="style1">Style 1 (video + demo track)</option>
                                <option value="style2">Style 2 (full-width image)</option>
                            </select>
                            {styleWarning && (
                                <p className="text-red-600 font-medium text-xs mt-1">
                                    ⚠️ This layout needs {styleWarning}.
                                </p>
                            )}
                        </div>

                        {/* Secondary Title (header subtitle, both layouts) */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Secondary Title (info page header subtitle)
                            </label>
                            <input
                                type="text"
                                value={editInputs[pack.id!]?.secondaryTitle || ''}
                                onChange={(e) => handleInputChange(pack.id!, 'secondaryTitle', e.target.value)}
                                placeholder="e.g. Vol 1"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                disabled={updating === pack.id}
                            />
                        </div>

                        {/* Style 2: Full-Width Image */}
                        {selectedStyle === 'style2' && (
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                    Full-Width Image (style 2 hero)
                                </label>
                                {pack.fullWidthImageUrl && !fileInputs[pack.id!]?.fullWidthFile && (
                                    <div className="mb-2">
                                        <img
                                            src={pack.fullWidthImageUrl}
                                            alt="Current full-width"
                                            className="w-full max-w-md rounded border border-gray-200 object-cover"
                                        />
                                        <p className="text-xs text-gray-400 mt-1">Current full-width image</p>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg"
                                    onChange={(e) => handleFullWidthChange(pack.id!, e.target.files?.[0] || null)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                    disabled={updating === pack.id}
                                />
                                {fileInputs[pack.id!]?.fullWidthFile && (
                                    <div className="mt-2">
                                        <img
                                            src={URL.createObjectURL(fileInputs[pack.id!].fullWidthFile!)}
                                            alt="New full-width preview"
                                            className="w-full max-w-md rounded border border-gray-200 object-cover"
                                        />
                                        <p className="text-xs text-green-600 mt-1">New full-width image selected</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Cover Image */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Cover Image
                            </label>
                            {pack.coverImageUrl && !fileInputs[pack.id!]?.coverFile && (
                                <div className="mb-2">
                                    <img
                                        src={pack.coverImageUrl}
                                        alt="Current cover"
                                        className="w-32 h-32 object-cover rounded border border-gray-200"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Current cover image</p>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/png,image/jpeg"
                                onChange={(e) => handleCoverChange(pack.id!, e.target.files?.[0] || null)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                disabled={updating === pack.id}
                            />
                            {fileInputs[pack.id!]?.coverFile && (
                                <div className="mt-2">
                                    <img
                                        src={URL.createObjectURL(fileInputs[pack.id!].coverFile!)}
                                        alt="New cover preview"
                                        className="w-32 h-32 object-cover rounded border border-gray-200"
                                    />
                                    <p className="text-xs text-green-600 mt-1">New cover image selected</p>
                                </div>
                            )}
                        </div>

                        {/* HLS Video (style 1 hero) */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                HLS Video Folder (.m3u8 + .ts segments)
                            </label>
                            <p className="text-xs text-gray-400 mb-1">
                                {pack.videoPlaylistUrl ? 'A video is already uploaded; selecting a folder replaces it.' : 'No video uploaded yet.'}
                            </p>
                            <input
                                key={`hls-${pack.id}-${pack.videoPlaylistUrl ?? ''}`}
                                type="file"
                                {...({ webkitdirectory: '', directory: '' } as any)}
                                multiple
                                onChange={(e) => handleVideoFolderChange(pack.id!, e.target.files)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                disabled={updating === pack.id}
                            />
                            {hlsErrors[pack.id!] && (
                                <p className="text-red-600 font-medium text-xs mt-1">⚠️ {hlsErrors[pack.id!]}</p>
                            )}
                            {(fileInputs[pack.id!]?.videoFiles.length ?? 0) > 0 && (
                                <p className="text-xs text-green-600 mt-1">
                                    ✓ Valid HLS folder ({fileInputs[pack.id!].videoFiles.length} files selected)
                                </p>
                            )}
                            {!hlsErrors[pack.id!] && hlsWarnings[pack.id!] && (
                                <p className="text-amber-600 font-medium text-xs mt-1">⚠️ {hlsWarnings[pack.id!]}</p>
                            )}
                        </div>

                        {/* Video Thumbnail */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Video Thumbnail (1920x1080 JPG)
                            </label>
                            {pack.videoThumbnailUrl && !fileInputs[pack.id!]?.thumbnailFile && (
                                <div className="mb-2">
                                    <img
                                        src={pack.videoThumbnailUrl}
                                        alt="Current thumbnail"
                                        className="w-64 h-36 object-cover rounded border border-gray-200"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Current video thumbnail</p>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/jpeg"
                                onChange={(e) => handleThumbnailChange(pack.id!, e.target.files?.[0] || null)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                disabled={updating === pack.id}
                            />
                            {thumbnailErrors[pack.id!] && (
                                <p className="text-red-600 font-medium text-xs mt-1">{thumbnailErrors[pack.id!]}</p>
                            )}
                            {fileInputs[pack.id!]?.thumbnailFile && !thumbnailErrors[pack.id!] && (
                                <div className="mt-2">
                                    <img
                                        src={URL.createObjectURL(fileInputs[pack.id!].thumbnailFile!)}
                                        alt="New thumbnail preview"
                                        className="w-64 h-36 object-cover rounded border border-gray-200"
                                    />
                                    <p className="text-xs text-green-600 mt-1">Valid 1920x1080 JPG selected</p>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => handleUpdate(pack.id!)}
                            disabled={updating === pack.id || !!thumbnailErrors[pack.id!] || !!hlsErrors[pack.id!] || !!styleWarning || createdAtInvalid}
                            className={`w-full px-4 py-2 rounded-md text-sm font-medium ${updating === pack.id || thumbnailErrors[pack.id!] || hlsErrors[pack.id!] || styleWarning || createdAtInvalid
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                        >
                            {updating === pack.id
                                ? (updateProgress || 'Updating...')
                                : 'Update Sound Pack'}
                        </button>
                    </div>
                </div>
                );
            })}
        </div>
    );
}

export default EditPreview;
